// src/components/atoms/Table/Table.tsx
import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import "@/components/atomic/Table/Table.scss";
import { IconButton, SortIcon } from "shared-portal-component";

type TableVariant = "app" | "admin";

interface TableProps<T extends object> {
  data: T[];
  columns: Array<ColumnDef<T>>;
  className?: string;
  loading?: boolean;
  error?: string | null;
  variant?: TableVariant;
  size?: "md" | "sm";
  /** Optional row click handler */
  onRowClick?: (row: T) => void;
}

function Table<T extends object>({
  data,
  columns,
  className = "",
  loading = false,
  error = null,
  variant = "app",
  size = "md",
  onRowClick,
}: TableProps<T>): React.ReactElement {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
    onSortingChange: setSorting,
  });

  const MIN_LENGTH = 0;

  if (loading) return <div className="table-card">Loading...</div>;
  if (error !== null) return <div className="table-card" style={{ color: "red" }}>{error}</div>;
  if (data.length === MIN_LENGTH) return <div className="table-card">No results found.</div>;

  return (
    <div
      className={`table-card table-card--${variant} table-card--${size} ${className}`}
    >
      <table>
        <colgroup>
          <col style={{ width: "230px" }} />
          <col style={{ width: "230px" }} />
          <col style={{ width: "230px" }} />
          <col style={{ width: "230px" }} />
          <col style={{ width: "100px" }} />
        </colgroup>

        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                return (
                  <th key={header.id}>
                    {canSort ? (
                      <span style={{ display: "inline-flex", alignItems: "center" }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <IconButton
                          icon={<SortIcon />}
                          ariaLabel="Sort"
                          size="sm"
                          variant="secondary"
                          className="search-input__button table-icon"
                          onClick={() => {
                            header.column.toggleSorting();
                          }}
                        />
                      </span>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={(onRowClick !== undefined) ? "clickable-row" : ""}
              onClick={() => {
                if (onRowClick !== undefined) onRowClick(row.original);
              }}
              style={{ cursor: (onRowClick !== undefined) ? "pointer" : "default" }}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
